import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormsModule } from '@angular/forms';
import { windows } from 'src/app/loginFiles/windows';
import { windowsLegacy } from 'src/app/loginFiles/windowsLegacy';
import { windowsAdminControl } from 'src/app/loginFiles/windowsAdminControl';
import { windowsAdminControlLegacy } from 'src/app/loginFiles/windowsAdminControlLegacy';
import { windowsGeneralLegacy } from 'src/app/loginFiles/windowsGeneralLegacy';
import { linux } from 'src/app/loginFiles/linux';
import { linuxAdminControl } from 'src/app/loginFiles/linuxAdminControl';
import { mac } from 'src/app/loginFiles/mac';
import { macAdminControl } from 'src/app/loginFiles/macAdminControl';
import { environment } from 'src/environments/environment';
import { ProjectService } from 'src/app/Services/project.service';
import { sysConfig } from 'src/app/sysConfig';
import {
  ActivationCreateHandoffContext,
  DownloadModalCloseResult,
} from '../create-user-dialog.types';
import { resolveEmployeeDisplayName } from 'src/app/utils/employee-display-name.util';
import { AvatarModule } from 'ngx-avatars';
import { trackingModeLabelForVersionType } from 'src/app/utils/tracking-mode-labels.util';

/** One install step the admin reads here and can copy to hand to the employee. */
export interface HandoffStep {
  title: string;
  detail: string;
  /** Easy-to-miss requirement shown as a callout under the step. */
  note?: string;
  required?: boolean;
}

export interface DownloadModalData {
  message: string;
  versionType: 'general' | 'silent' | 'restricted';
  operatingSystem: 'windows' | 'mac' | 'linux' | 'windows_7_8';
  email?: string;
  password?: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  activationHandoff?: ActivationCreateHandoffContext;
}

@Component({
  selector: 'app-download-modal',
  imports: [CommonModule, MatButtonModule, MatIconModule, MatRadioModule, MatTooltipModule, FormsModule, AvatarModule],
  templateUrl: './download-modal.component.html',
  styleUrl: './download-modal.component.css'
})
export class DownloadModalComponent implements OnInit {
  selectedVersionType: 'general' | 'silent' | 'restricted' = 'general';
  selectedOs: 'windows' | 'mac' | 'linux' | 'windows_7_8' | string = 'windows';
  activeWindowsTab: 'win_10_11' | 'win_7_8' = 'win_10_11';
  downloadingFileName: string = '';
  rustDownloadingFileName: string = '';
  /** Handoff only: the OS the admin already picked stays collapsed until Change. */
  showOsPicker = false;
  downloadInFlight = false;
  stepsCopied = false;
  private downloadedWindowsTab: 'win_10_11' | 'win_7_8' | null = null;
  sysConfig = sysConfig;
  private autoDownloadAttempted = false;
  private downloadTriggered = false;

  constructor(
    public dialogRef: MatDialogRef<DownloadModalComponent, DownloadModalCloseResult>,
    @Inject(MAT_DIALOG_DATA) public data: DownloadModalData,
    private projectService: ProjectService
  ) {
    this.selectedVersionType = this.data.versionType;
    this.selectedOs = this.data.operatingSystem;
    if (this.data.operatingSystem === 'windows_7_8') {
      this.activeWindowsTab = 'win_7_8';
    } else {
      this.activeWindowsTab = 'win_10_11';
    }
  }

  get isWindows(): boolean {
    return (
      this.data.operatingSystem === 'windows' ||
      this.data.operatingSystem === 'windows_7_8' ||
      this.selectedOs === 'windows' ||
      this.selectedOs === 'windows_7_8'
    );
  }

  setActiveWindowsTab(tab: 'win_10_11' | 'win_7_8'): void {
    this.activeWindowsTab = tab;
  }

  get isActivationHandoff(): boolean {
    return !!this.data?.activationHandoff;
  }

  get employeeDisplayName(): string {
    return resolveEmployeeDisplayName(this.data, '');
  }

  get modeLabel(): string {
    return trackingModeLabelForVersionType(this.data.versionType);
  }

  get activationTitle(): string {
    return this.isActivationHandoff
      ? 'Package ready'
      : '🎉 Employee Created Successfully!';
  }

  get activationModeReassurance(): string {
    if (this.data.versionType === 'silent') {
      return 'Hidden — they won’t see SuperSee or sign in.';
    }
    if (this.data.versionType === 'restricted') {
      return 'You install it on their computer — they don’t manage SuperSee.';
    }
    return 'They can sign in after install — or you can install it for them.';
  }

  get activationPromisedLand(): string {
    return 'This gets one real employee onto SuperSee — so you can see a real workday after they’re Connected.';
  }

  get activationOsLabel(): string {
    if (this.data.operatingSystem === 'mac') return 'macOS';
    if (this.data.operatingSystem === 'linux') return 'Linux';
    return 'Windows';
  }

  get activationStepsLead(): string {
    return `Details for ${this.activationOsLabel}. If a file already downloaded, use that — otherwise use Download in step 1.`;
  }

  get activationNextStepCopy(): string {
    if (this.data.versionType === 'silent') {
      return 'No login for them. Run the downloaded file on their machine, then return.';
    }
    return 'Install on their machine or share the login, then return.';
  }

  /** Match employee list/profile: Hidden (silent) Win 10/11 Rust stays off until ready. */
  get showWindowsRustAgentPanel(): boolean {
    if (!this.sysConfig.rustAgentBetaEnabled) {
      return false;
    }
    if (this.data.versionType === 'silent' && !this.sysConfig.rustAgentBetaHiddenMenuEnabled) {
      return false;
    }
    return true;
  }

  /** File name of the installer that matches what the admin is looking at, or '' if none yet. */
  get downloadedFileName(): string {
    if (this.isWindows && this.downloadedWindowsTab !== this.activeWindowsTab) {
      return '';
    }
    if (this.isWindows && this.activeWindowsTab === 'win_10_11') {
      return this.rustDownloadingFileName || this.downloadingFileName;
    }
    return this.downloadingFileName;
  }

  get hasDownloaded(): boolean {
    return !!this.downloadedFileName;
  }

  /** Steps for the Windows version on screen; other systems keep the legacy step list. */
  get handoffSteps(): HandoffStep[] {
    if (!this.isWindows) {
      return [];
    }
    const mode = this.data.versionType;
    const restart: HandoffStep = {
      title: 'Restart the computer',
      detail: 'This finishes the install.',
      required: true,
    };

    if (this.activeWindowsTab === 'win_7_8') {
      const outcome: Record<DownloadModalData['versionType'], HandoffStep> = {
        general: { title: 'They sign in', detail: 'The employee signs in with the credentials you sent them.' },
        restricted: {
          title: 'They sign in once',
          detail: 'The employee signs in once with the credentials you sent them. Tracking then runs on its own.',
        },
        silent: {
          title: 'It runs silently',
          detail: 'SuperSee runs in the background with no login. Delete the installer file afterwards.',
        },
      };
      return [
        { title: 'Run as administrator', detail: 'Right-click the downloaded file and choose Run as Administrator.' },
        { title: 'If Windows shows a warning', detail: 'Click More Info, then Run Anyway, then Yes on both pop-ups.' },
        { title: 'Let it finish', detail: 'A terminal window runs 8 steps and closes by itself.' },
        outcome[mode],
        restart,
      ];
    }

    const secretsFile = mode === 'silent' ? 'Settings-Deploy.secrets.json' : 'SuperSee-Deploy.secrets.json';
    const steps: HandoffStep[] = [];
    if (mode !== 'general') {
      steps.push({
        title: 'Unzip the download',
        detail: 'Right-click the .zip, choose Extract All, then open the extracted folder.',
      });
    }
    steps.push({
      title: 'Run the installer',
      detail: 'Double-click the .msi. When Windows asks to allow the app, choose Yes.',
      note: mode !== 'general'
        ? `Keep ${secretsFile} in the same folder as the .msi. The install fails without it.`
        : undefined,
    });
    steps.push({
      title: 'If SmartScreen appears',
      detail: 'Click More info, then Run anyway, then Yes on the permission prompt.',
    });
    if (mode === 'general') {
      steps.push({ title: 'They sign in', detail: 'The employee signs in with the credentials you sent them.' });
    } else if (mode === 'restricted') {
      steps.push({ title: 'It starts on its own', detail: 'SuperSee starts with this employee’s account automatically.' });
    } else {
      steps.push({
        title: 'It runs silently',
        detail: 'No employee login needed. The app runs in the background and appears as “Settings”.',
      });
    }
    steps.push(restart);
    return steps;
  }

  get canCopySteps(): boolean {
    return this.isActivationHandoff && this.handoffSteps.length > 0;
  }

  /** Plain-text version of the steps, ready to paste into chat or email. Never includes credentials. */
  get handoffStepsText(): string {
    const lines = [`Install SuperSee on your computer (${this.activeWindowsLabel})`];
    if (this.downloadedFileName) {
      lines.push(`File: ${this.downloadedFileName}`);
    }
    lines.push('');
    this.handoffSteps.forEach((step, index) => {
      lines.push(`${index + 1}. ${step.title}${step.required ? ' (required)' : ''}`);
      lines.push(`   ${step.detail}`);
      if (step.note) {
        lines.push(`   Note: ${step.note}`);
      }
    });
    return lines.join('\n');
  }

  async copySteps(): Promise<void> {
    try {
      await window.navigator.clipboard.writeText(this.handoffStepsText);
      this.stepsCopied = true;
      setTimeout(() => (this.stepsCopied = false), 2000);
    } catch {
      this.stepsCopied = false;
    }
  }

  get activeWindowsLabel(): string {
    return this.activeWindowsTab === 'win_7_8' ? 'Windows 7 & 8' : 'Windows 10 & 11';
  }

  ngOnInit(): void {
    this.autoDownloadAttempted = true;
    void this.startDownload();
  }

  /** Downloads the installer for what is currently shown (also used by the handoff Download button). */
  async startDownload(): Promise<void> {
    this.downloadInFlight = true;
    try {
      if (this.isWindows) {
        this.downloadedWindowsTab = this.activeWindowsTab;
        if (this.activeWindowsTab === 'win_7_8') {
          this.downloadLegacy();
        } else if (this.showWindowsRustAgentPanel) {
          await this.downloadRust();
        } else {
          this.download();
        }
      } else {
        this.download();
      }
    } finally {
      this.downloadInFlight = false;
    }
  }

  close(): void {
    this.dialogRef.close({
      reason: 'closed',
      autoDownloadAttempted: this.autoDownloadAttempted,
      downloadTriggered: this.downloadTriggered,
    });
  }

  copyToClipboard(value: string): void {
    window.navigator.clipboard.writeText(value);
  }

  download(osOverride?: string): void {
    const osMapping: Record<string, string> = {
      windows: 'Windows',
      windows_7_8: 'Windows 7 & 8',
      mac: 'Mac',
      linux: 'Linux'
    };
    const os = osOverride || osMapping[this.selectedOs] || 'Windows';

    if (this.selectedVersionType === 'general') {
      this.downloadGeneral(os);
    } else if (this.selectedVersionType === 'restricted') {
      this.downloadRestricted(os);
    } else {
      this.downloadSilent(os);
    }
  }

  downloadLegacy(): void {
    this.download('Windows 7 & 8');
  }

  private markDownloadTriggered(): void {
    this.downloadTriggered = true;
  }

  private downloadGeneral(os: string): void {
    let downloadUrl = '';
    let fileName = '';

    if (os === 'Windows' || os === 'Windows 7 & 8') {
      if (os === 'Windows 7 & 8') {
        const blob = new Blob([windowsGeneralLegacy], { type: 'text/plain' });
        fileName = 'Supersee_Windows_7_8_Employee_Controlled_Installer.bat';
        this.downloadingFileName = fileName;
        this.markDownloadTriggered();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(a.href);
        return;
      }
      downloadUrl = 'assets/Windows/General/Supersee_Installer.bat';
      fileName = `Supersee_Windows_Employee_Controlled_Installer.bat`;
    } else if (os === 'Mac') {
      downloadUrl = environment.app.macInstallerUrl;
      fileName = 'Supersee_Mac_Employee_Controlled_Installer.dmg';
    } else if (os === 'Linux') {
      downloadUrl = environment.app.linuxInstallerUrl;
      fileName = 'Supersee_Linux_Employee_Controlled_Installer.deb';
    } else {
      console.error("Unsupported OS type for general agent download:", os);
      return;
    }

    this.downloadingFileName = fileName;
    this.markDownloadTriggered();
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = fileName;

    if (os === 'Mac' || os === 'Linux') {
      a.target = '_blank';
    }

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  private downloadRestricted(os: string): void {
    let content = '';
    let restrictedFileName = '';
    const fileType = 'text/plain';
    const timestamp = new Date().getTime();

    const email = this.data.email;
    const password = this.data.password;
    const employeeName = (this.data.first_name && this.data.last_name ? `${this.data.first_name}_${this.data.last_name}` : this.data.name || 'employee');

    if (!email || !password) {
      console.error("Email and password are required for restricted (Admin Control) installer.");
      return;
    }

    if (os === 'Windows' || os === 'Windows 7 & 8') {
      const winAdminTpl = os === 'Windows 7 & 8' ? windowsAdminControlLegacy : windowsAdminControl;
      content = winAdminTpl.replace(/%EMAIL%/g, email).replace(/%PASSWORD%/g, password);
      restrictedFileName = os === 'Windows 7 & 8'
        ? `${employeeName.replace(/ /g, '_')}_Windows_7_8_Admin_Controlled_${timestamp}.bat`
        : `${employeeName.replace(/ /g, '_')}_Windows_Admin_Controlled_${timestamp}.bat`;
    } else if (os === 'Mac') {
      content = macAdminControl.replace(/\$EMAIL/g, email).replace(/\$PASSWORD/g, password);
      restrictedFileName = `${employeeName.replace(/ /g, '_')}_Mac_Admin_Controlled_${timestamp}.command`;
    } else if (os === 'Linux') {
      content = linuxAdminControl.replace(/\$EMAIL/g, email).replace(/\$PASSWORD/g, password);
      restrictedFileName = `${employeeName.replace(/ /g, '_')}_Linux_Admin_Controlled_${timestamp}.sh`;
    } else {
      console.error("Unsupported OS type for restricted agent download:", os);
      return;
    }

    this.downloadingFileName = restrictedFileName;
    this.markDownloadTriggered();
    const blob = new Blob([content], { type: fileType });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = restrictedFileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  }

  private downloadSilent(os: string): void {
    let content = '';
    let silentFileName = '';
    const fileType = 'text/plain';
    const timestamp = new Date().getTime();

    const email = this.data.email;
    const password = this.data.password;
    const employeeName = (this.data.first_name && this.data.last_name ? `${this.data.first_name}_${this.data.last_name}` : this.data.name || 'employee');

    if (!email || !password) {
      console.error("Email and password are required for silent installer.");
      return;
    }

    if (os === 'Windows' || os === 'Windows 7 & 8') {
      const winSilentTpl = os === 'Windows 7 & 8' ? windowsLegacy : windows;
      content = winSilentTpl.replace(/%EMAIL%/g, email).replace(/%PASSWORD%/g, password);
      silentFileName = os === 'Windows 7 & 8'
        ? `${employeeName.replace(/ /g, '_')}_Windows_7_8_Silent_${timestamp}.bat`
        : `${employeeName.replace(/ /g, '_')}_Windows_Silent_${timestamp}.bat`;
    } else if (os === 'Mac') {
      content = mac.replace(/\$EMAIL/g, email).replace(/\$PASSWORD/g, password);
      silentFileName = `${employeeName.replace(/ /g, '_')}_Mac_Silent_${timestamp}.command`;
    } else if (os === 'Linux') {
      content = linux.replace(/\$EMAIL/g, email).replace(/\$PASSWORD/g, password);
      silentFileName = `${employeeName.replace(/ /g, '_')}_Linux_Silent_${timestamp}.sh`;
    } else {
      console.error("Unsupported OS type for silent agent download:", os);
      return;
    }

    this.downloadingFileName = silentFileName;
    this.markDownloadTriggered();
    const blob = new Blob([content], { type: fileType });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = silentFileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  }

  async downloadRust(): Promise<void> {
    if (this.data.operatingSystem !== 'windows' || !this.showWindowsRustAgentPanel) {
      return;
    }

    const employee = {
      email: this.data.email,
      username: this.data.email,
      name: this.data.name,
      first_name: this.data.first_name,
      last_name: this.data.last_name,
    };

    if (this.data.versionType === 'general') {
      this.rustDownloadingFileName =
        (await this.projectService.downloadRustAgentInstaller(null, 'general')) || '';
      if (this.rustDownloadingFileName) {
        this.markDownloadTriggered();
      }
      return;
    }

    const email = this.data.email;
    const password = this.data.password;
    if (!email || !password) {
      console.error('Email and password are required for Rust silent/restricted installer.');
      return;
    }

    const mode = this.data.versionType === 'restricted' ? 'restricted' : 'silent';
    this.rustDownloadingFileName =
      (await this.projectService.downloadRustAgentInstaller(employee, mode, password)) || '';
    if (this.rustDownloadingFileName) {
      this.markDownloadTriggered();
    }
    return;
  }
}
